import { DateTime } from 'luxon';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRelayEnvironment } from 'react-relay';
import { v4 as uuidv4 } from 'uuid';

import { CalendarTableCustomButtonEditView } from '../calendar/calendarTableCustomButtonEditView';
import EllipseContainer from '../components/ellipseContainer';
import ListAssigneeWithAvatar from '../components/listAssigneeWithAvatar';
import { FORMAT_TIME_DISPLAY } from '../const';
import { TasksQuery$variables } from '../graphql/__generated__/TasksQuery.graphql';
import { TaskConnectionFragment } from '../graphql/fragments/TaskConnectionFragment';
import { TaskFragment } from '../graphql/fragments/TaskFragment';
import { TaskFragmentScheduleItem } from '../graphql/fragments/TaskFragmentScheduleItem';
import { TaskFragmentStatus } from '../graphql/fragments/TaskFragmentStatus';
import { TaskFragmentType } from '../graphql/fragments/TaskFragmentType';
import { TaskListFragment } from '../graphql/fragments/TaskListFragment';
import { TasksQuery } from '../graphql/TasksQuery';
import useAccess from '../hooks/useAccess';
import { FORMAT_DATE, getObjectFromRelayStoreById, LIST_TASK_GROUP_STATUS, LIST_TASK_TYPE, UserSelect } from '../index';
import ListItem from '../outListItem';
import ProjectSelect from '../outProjectSelect';
import TableDb from '../outTableDb';
import { SmxGlobalFilterContext } from '../providers/globalFilterProvider';
import { SmxPermissionContext } from '../providers/permissionProvider';
import { ComponentsDispatch, RootState } from '../store';
import { setterCalendar } from '../store/slices/calendarSlice';
import { TaskBoardTaskAssignee } from '../taskBoard/component/taskBoardTaskAssignee';
import { ActionsType, ItemTaskType, OnTasksImport } from '../types';

export interface ICalendarTable {
    onTasksImport?: OnTasksImport;
}

const CalendarTable: React.FC<ICalendarTable> = ({ onTasksImport }) => {
    const environment = useRelayEnvironment();
    const dispatch = useDispatch<ComponentsDispatch>();
    const { userId } = useContext(SmxPermissionContext);
    const { filterProject } = useContext(SmxGlobalFilterContext);
    const currentTask = useSelector<RootState, ItemTaskType | null>((state) => state.calendar.currentTask);
    const availableHeightTable = useSelector<RootState, number>((state) => state.calendar.availableHeightTable);
    const externalTableTaskPrefixFetchKey = useSelector<RootState, string | undefined>(
        (state) => state.calendarTasks.externalTableTaskPrefixFetchKey,
    );
    const tasks = useSelector<RootState, ItemTaskType[]>((state) => state.calendar.tasks);
    const updateTaskFetchKey = useSelector<RootState, number>((state) => state.setting.updateTaskFetchKey);

    const isTaskEditRef = useRef<boolean>(!!currentTask);
    const [fetchKey, setFetchKey] = useState(0);
    const [prefixFetchKey, setPrefixFetchKey] = useState('');

    isTaskEditRef.current = !!currentTask;

    useEffect(() => {
        forceUpdateDataTable(externalTableTaskPrefixFetchKey);
    }, [externalTableTaskPrefixFetchKey]);

    useEffect(() => {
        setFetchKey(fetchKey + 1);
    }, [updateTaskFetchKey]);

    // simplify type to any because Flatten type take a lot resources of TypeScript
    // to test use sandbox with type
    type TaskFragmentData = any;
    // Flatten<TaskFragmentStatus$data> &
    //     Flatten<TaskFragment$data> &
    //     Flatten<TaskFragmentType$data> &
    //     Flatten<TaskFragmentScheduleItem$data>;

    const handleClickForEdit = (data: any) => {
        if (isTaskEditRef.current) {
            setFetchKey((oldFetchKey) => ++oldFetchKey);
        }

        const interval = setInterval(() => {
            if (!isTaskEditRef.current) {
                isTaskEditRef.current = false;
                clearInterval(interval);
                console.log('finish editing task');
                setFetchKey((oldFetchKey) => ++oldFetchKey);
                setTimeout(() => {
                    setFetchKey(0);
                }, 1000);
                return;
            }
        }, 1000);

        dispatch(setterCalendar({ currentTask: getObjectFromRelayStoreById(environment, data.keyRaw) }));
    };

    // const filterFlexible = filterProject?.length ? `[{"or":[{"project_id":"${filterProject.join('|')}"}]}]` : undefined;

    const filterFlexibleCommon = [];

    filterProject?.length && filterFlexibleCommon.push(`{"or":[{"project_id":"${filterProject.join('|')}"}]}`);

    const accessAll = useAccess(['schedule-task.all.view']);
    const accessOwner = useAccess(['schedule-task.owner']);
    const accessAssignee = useAccess(['schedule-task.assignee']);

    if (!accessAll) {
        const filterTaskOwnerAssignee = [];
        accessOwner && filterTaskOwnerAssignee.push(`{"owner_task_id":"${userId}"}`);
        accessAssignee && filterTaskOwnerAssignee.push(`{"assignee_for_task":"*${userId}"}`);

        (accessAssignee || accessOwner) && filterFlexibleCommon.push(`{"or":[${filterTaskOwnerAssignee.join(',')}]}`);
    }

    const filterFlexible = filterFlexibleCommon?.length ? `[${filterFlexibleCommon.join(',')}]` : undefined;

    const forceUpdateDataTable = (uuid?: string) => {
        setPrefixFetchKey(uuid || uuidv4());
    };

    const handleTasksImport: OnTasksImport = async (args) => {
        await onTasksImport?.(args);

        forceUpdateDataTable();
    };

    const access = useAccess(['schedule-task.create', 'schedule-task.edit', 'schedule-task.manage']);

    const getActions = (): Exclude<ActionsType, 'sorter' | 'filter'>[] => {
        const baseActions: Exclude<ActionsType, 'sorter' | 'filter'>[] = ['columnsView', 'export-csv'];

        if (access) {
            baseActions.push('import-csv');
        }
        return baseActions;
    };

    const filterFlexibleProject = filterProject?.length ? `[{"or":[{"id":"${filterProject.join('|')}"}]}]` : undefined;

    return (
        <div style={{ userSelect: 'none' }}>
            <TableDb<TasksQuery$variables, TaskFragmentData, {}>
                query={TasksQuery}
                fragmentList={TaskListFragment}
                fragmentConnection={TaskConnectionFragment}
                fragment={[TaskFragment, TaskFragmentType, TaskFragmentStatus, TaskFragmentScheduleItem]}
                nameQuery={'tasks'}
                columnsLabel={{
                    datetime: 'Date',
                    name: 'Task Name',
                    'statusItem.name': 'Task Status',
                    duration: 'Duration',
                    'typeItem.name': 'Task Type',
                    start_datetime: 'Start Time',
                    end_datetime: 'End Time',
                    'project.name': 'Project name',
                    'project.number': 'Project number',
                    'scheduleItem.typeItem.name': 'Item Type',
                    'scheduleItem.statusItem.name': 'Item Status',
                    'scheduleItem.name': 'Item Name',
                    assignee_for_task: 'Assignee',
                    owner_task_id: 'Owner',
                }}
                defaultHiddenColumn={[
                    'end_datetime',
                    'project.number',
                    'scheduleItem.typeItem.name',
                    'assignee_for_task',
                    'owner_task_id',
                ]}
                paramFilter={{
                    name: {},
                    datetime: {},
                    type: {},
                    'project.number': {},
                    'scheduleItem.name': {},
                    'project.name': {
                        filterField: 'project_id',
                        filterComponent: (
                            <ProjectSelect
                                filterList={filterProject ? [] : undefined}
                                filterFlexible={filterFlexibleProject}
                                // localFilters={{ id: filterProject }}
                            />
                        ),
                    },
                    'statusItem.name': {
                        filterField: 'status',
                        filterComponent: <ListItem nameList={LIST_TASK_GROUP_STATUS} readonly />,
                        availableTypeFilter: ['from_list', 'has_any_value', 'is_unknown'],
                    },
                    'typeItem.name': {
                        filterField: 'type',
                        filterComponent: <ListItem nameList={LIST_TASK_TYPE} readonly />,
                        availableTypeFilter: ['from_list', 'has_any_value', 'is_unknown'],
                    },
                    assignee_for_task: {
                        filterField: 'assignee_for_task', //'users.user_id',
                        filterComponent: <UserSelect />,
                        availableTypeFilter: ['from_list', 'has_any_value', 'is_unknown'],
                        useAsString: true,
                    },
                    owner_task_id: {
                        filterField: 'owner_task_id',
                        filterComponent: <UserSelect />,
                        availableTypeFilter: ['from_list', 'has_any_value', 'is_unknown'],
                    },
                }}
                paramSorter={['name', 'duration', 'datetime']}
                actions={getActions()}
                customActionComponents={[
                    (task, data) => (
                        <CalendarTableCustomButtonEditView
                            data={data}
                            task={task as ItemTaskType}
                            onClick={handleClickForEdit}
                        />
                    ),
                ]}
                renderDataColumn={{
                    datetime: (value: string) => {
                        return <>{DateTime.fromSQL(value).toFormat(FORMAT_DATE)}</>;
                    },
                    start_datetime: (value: string) => {
                        return <>{DateTime.fromSQL(value).toFormat(FORMAT_TIME_DISPLAY)}</>;
                    },
                    end_datetime: (value: string) => {
                        return <>{DateTime.fromSQL(value).toFormat(FORMAT_TIME_DISPLAY)}</>;
                    },
                    'statusItem.name': (value: string) => {
                        return <EllipseContainer>{value}</EllipseContainer>;
                    },
                    name: (value: string) => {
                        return <EllipseContainer>{value}</EllipseContainer>;
                    },
                    assignee_for_task: (value: string) => {
                        const listIdAssignee = value.split(',');

                        return <ListAssigneeWithAvatar listIdAssignee={listIdAssignee} />;
                    },
                    owner_task_id: (value: string) => {
                        return <TaskBoardTaskAssignee userId={value} noDisplayEmptyIcon displayHint />;
                    },
                }}
                sizeOfPage={['10', '20', '50']}
                // customActions={[
                //     {
                //         id: 'edit',
                //         hint: 'Edit Item',
                //         callback: handleClickForEdit,
                //         icon: <EditOutlined />,
                //         accessToButton: (item) =>
                //             useAccessForTask(item as ItemTaskType, [
                //                 'schedule-task.assignee.edit',
                //                 'schedule-task.assignee.manage',
                //                 'schedule-task.all.edit',
                //                 'schedule-task.all.manage',
                //                 'schedule-task.owner.edit',
                //                 'schedule-task.owner.manage',
                //             ]),
                //     },
                // ]}
                onClickRow={handleClickForEdit}
                externalFetchKey={fetchKey}
                heightTable={availableHeightTable}
                varRequest={{ filter_flexible: filterFlexible }}
                onSelect={(record) => console.log(record)}
                onSelectionChange={(selectedRowKeys) => console.log(selectedRowKeys)}
                fixedColumn={{
                    name: 'left',
                    duration: 'left',
                }}
                onTasksImport={handleTasksImport}
                prefixFetchKey={prefixFetchKey}
            />
        </div>
    );
};

export default CalendarTable;
